import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FlowTrace, FreightStateRail, ProofStack, type ProofItem } from "@/components/moveload/flow";
import type { FreightState } from "@/lib/flowUi";
import { cn } from "@/lib/utils";

/**
 * VX-3 — Homepage product demonstration.
 *
 * Shows an anonymous visitor how a Moveload load actually behaves: state rail,
 * flow trace, proof stack and where Moveload AI observes. This is an ILLUSTRATIVE
 * WALKTHROUGH of the product's workflow — it is labelled as such and renders
 * no network figures, prices, ETAs, carrier names or map data. Nothing here is
 * presented as live data or as a commitment.
 */

interface DemoStage {
  key: string;
  title: string;
  state: FreightState;
  completed: FreightState[];
  progress: number;
  moving: boolean;
  narrative: string;
  aiNote: string;
  proof: ProofItem[];
}

const STAGES: DemoStage[] = [
  {
    key: "planned",
    title: "1 · Described and priced",
    state: "planned",
    completed: [],
    progress: 0,
    moving: false,
    narrative:
      "You describe the load. Moveload prices it line by line and holds that price as evidence, so what you accept is what you are billed.",
    aiNote: "Moveload AI checks the description for missing weight, dimensions or handling detail. You confirm — Moveload AI never books.",
    proof: [
      { label: "Price breakdown", status: "proven", detail: "Every line item visible before you commit" },
      { label: "Vehicle fit check", status: "proven", detail: "Matched by weight, dimensions and corridor" },
      { label: "Transporter assigned", status: "pending", detail: "Only verified operators enter the pool" },
    ],
  },
  {
    key: "moving",
    title: "2 · Verified and moving",
    state: "moving",
    completed: ["planned"],
    progress: 0.56,
    moving: true,
    narrative:
      "Driver, vehicle and documents are verified before pickup. A barcode handover starts the trip and tracking runs against the plan.",
    aiNote: "Moveload AI watches for drift against the plan and raises attention early. Escalation stays a human decision.",
    proof: [
      { label: "Price breakdown", status: "proven", detail: "Locked at booking" },
      { label: "Driver & vehicle verified", status: "proven", detail: "Licence, PrDP and roadworthy checked" },
      { label: "Pickup barcode scan", status: "proven", detail: "Geofenced at the collection point" },
      { label: "Delivery proof", status: "pending", detail: "Captured on arrival" },
    ],
  },
  {
    key: "proven",
    title: "3 · Proven and paid",
    state: "proven",
    completed: ["planned", "moving"],
    progress: 1,
    moving: false,
    narrative:
      "The receiver code and signed POD close the chain. Payout is released against proof — not against a promise.",
    aiNote: "Moveload AI reads the POD for quality and flags anything unreadable. The ledger entry stays the authority.",
    proof: [
      { label: "Price breakdown", status: "proven", detail: "Reconciled to the booked snapshot" },
      { label: "Driver & vehicle verified", status: "proven" },
      { label: "Pickup barcode scan", status: "proven" },
      { label: "Signed POD & receiver code", status: "proven", detail: "Releases the controlled payout" },
    ],
  },
];

/**
 * Concrete, non-numeric outcomes of Moveload AI. No fabricated percentages,
 * savings or timings — only capability statements the product actually performs.
 */
const AI_OUTCOMES = [
  {
    key: "ai_outcome_quoting",
    title: "Faster quoting",
    detail: "Moveload AI completes missing load detail so a line-by-line price comes back in one pass.",
    cta: "Get a quote",
  },
  {
    key: "ai_outcome_matching",
    title: "Smarter matching",
    detail: "Moveload AI ranks verified transporters by vehicle fit, corridor and readiness before you choose.",
    cta: "Match a load",
  },
  {
    key: "ai_outcome_proof",
    title: "Cleaner proof",
    detail: "Moveload AI checks POD quality and flags unreadable evidence before payout is requested.",
    cta: "See the proof chain",
  },
] as const;

const AUTO_ADVANCE_MS = 6000;


export function ProductDemonstration({ onStart }: { onStart: (source: string) => void }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  useEffect(() => {
    if (paused || reduced) return;
    const t = window.setTimeout(() => setIndex(i => (i + 1) % STAGES.length), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [index, paused, reduced]);

  const stage = STAGES[index];

  return (
    <section
      id="how-a-load-moves"
      aria-labelledby="demo-heading"
      className="bg-canvas border-y border-border"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="container py-20 md:py-28">
        <div className="max-w-3xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-live">See the product</p>
          <h2 id="demo-heading" className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-[0.95]">
            How a load moves
            <br />
            <span className="text-primary">from quote to proof.</span>
          </h2>
          <p className="mt-6 text-muted-foreground max-w-xl">
            Every Moveload shipment carries the same three states and the same evidence trail. Step through it before you register.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Stage selector */}
          <div className="lg:col-span-4">
            <ol className="space-y-2" aria-label="Shipment walkthrough stages">
              {STAGES.map((s, i) => (
                <li key={s.key}>
                  <button
                    type="button"
                    aria-current={i === index ? "step" : undefined}
                    onClick={() => setIndex(i)}
                    className={cn(
                      "w-full min-h-12 rounded-lg border px-4 py-3 text-left text-sm font-semibold transition press-sm",
                      i === index
                        ? "border-primary/30 bg-card text-foreground shadow-elevation-1"
                        : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {s.title}
                  </button>
                </li>
              ))}
            </ol>

            <p className="mt-6 text-xs text-muted-foreground">
              Illustrative walkthrough of the Moveload workflow. Not live network data, and not a price, ETA or service
              commitment.
            </p>
          </div>

          {/* Stage canvas */}
          <div className="lg:col-span-8">
            <div className="rounded-panel bg-card border border-border shadow-elevation-2 p-6 md:p-8">
              <FreightStateRail current={stage.state} completed={stage.completed} />

              <div className="mt-6">
                <FlowTrace
                  progress={stage.progress}
                  moving={stage.moving && !reduced}
                  originLabel="Pickup"
                  destinationLabel="Delivery"
                />
                <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  <span>Pickup</span>
                  <span>Delivery</span>
                </div>
              </div>

              <p aria-live="polite" className="mt-6 text-sm md:text-base text-foreground leading-relaxed">
                {stage.narrative}
              </p>

              <div className="mt-5 rounded-lg border border-border bg-secondary/40 p-4">
                <div className="flex items-start gap-3">
                  <Brain className="h-4 w-4 mt-0.5 shrink-0 text-cnoi" aria-hidden />
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Where Moveload AI helps: </span>
                    {stage.aiNote}
                  </p>
                </div>

                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                    What Moveload AI does for you
                  </p>
                  <ul className="mt-3 grid gap-3 sm:grid-cols-3">
                    {AI_OUTCOMES.map(o => (
                      <li
                        key={o.key}
                        className="rounded-lg border border-border bg-card p-4 flex flex-col justify-between gap-3"
                      >
                        <div>
                          <p className="text-sm font-semibold text-foreground">{o.title}</p>
                          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{o.detail}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-10 w-full press-sm"
                          onClick={() => onStart(o.key)}
                        >
                          {o.cta}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>


              <div className="mt-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-2">Evidence trail</p>
                <ProofStack items={stage.proof} />
              </div>

              <Button
                size="lg"
                className="group mt-7 w-full sm:w-auto min-h-12 bg-accent text-accent-foreground hover:bg-accent/90 press"
                onClick={() => onStart("product_demo")}
              >
                Start a load like this <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductDemonstration;
